import { expiresIn } from '@solink/vd-data-layer/api-clients/shared/AppCache';
import {
  ALL_EVENTS_REPORT,
  ALL_LOCATIONS,
  HISTOGRAM_RANGE,
  HistogramApiHelper,
  type HistogramRange,
  histogramRangeToMomentDuration,
  type IHistogramApiParams,
  type ITopNApiParams,
  type IUnitsAndCount,
  type SearchOperator,
} from '@solinkcorp/vd-common';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import moment from 'moment-timezone';
/* UTILS */
import FeaturesUtil from 'utils/FeaturesUtil';
/* STORES */
import EventFilterStore from 'stores/EventFilterStore';
import { addSearchToQuery, applyTagsFilter, type Filter } from 'utils/EventsFetchUtil';
/* LOCAL STORAGE */
import { type EventStandardDefs, type FeatureStandardDefs } from '@solinkcorp/event-standard';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { type EventFilterState } from './types';

export const getTopHistogramParams = ({
  eventStandard,
  featureStandard,
  date: sourceDate,
  dataPaths,
  fieldName, // API defaults to `details.Total amount`
  groupName,
  siteIds,
  metric,
  order, // API defaults to `desc`
  range, // API defaults to `day`
  rangeRolling,
  reportTemplateId,
  size, // API defaults to `3`
  timezoneId,
}: {
  eventStandard: EventStandardDefs;
  featureStandard: FeatureStandardDefs;
  date: Date | number;
  dataPaths?: readonly string[];
  fieldName?: string;
  groupName?: string;
  siteIds?: string[];
  metric: string;
  order?: string;
  range: HistogramRange;
  rangeRolling?: boolean;
  reportTemplateId?: string;
  size?: number;
  timezoneId: string;
}): ITopNApiParams => {
  /** default use start time of the widget range for the query */
  let date = toMoment(sourceDate, timezoneId)
    .startOf(histogramRangeToMomentDuration(range))
    .toDate();
  let origin: 'start' | 'end' = 'start';

  if (rangeRolling) {
    date = toMoment(sourceDate, timezoneId).endOf(HistogramApiHelper.getBucketSize(range)).toDate();
    origin = 'end';
  }

  // Filter out the 'all-locations' and check if there is any remaining to use as params
  const realSitesIds = siteIds?.filter((siteId) => siteId !== ALL_LOCATIONS);

  let includeTags = fieldName ? (eventStandard[fieldName]?.features?.histogramQueryTags ?? []) : [];
  let eventTypes, eventSubtypeQuery;
  if (reportTemplateId && reportTemplateId !== ALL_EVENTS_REPORT) {
    const {
      reportHistogramTags,
      reportEventTypes,
      reportEventSubtypeQueries,
      reportMergeWithSubtypes,
    } = EventFilterStore.getReportFilters(featureStandard, reportTemplateId);
    eventTypes = reportEventTypes;
    eventSubtypeQuery = reportEventSubtypeQueries[0];
    includeTags = includeTags.concat(reportHistogramTags);

    if (reportMergeWithSubtypes) {
      eventSubtypeQuery = `${eventSubtypeQuery}|${reportMergeWithSubtypes.join('|')}`;
    }
  }

  return HistogramApiHelper.generateTopNApiParams({
    date,
    origin,
    dataPaths,
    eventSubtypeQuery,
    fieldName,
    groupName,
    includeTags,
    siteIds: realSitesIds,
    metric,
    order,
    range,
    rangeRolling,
    reportTemplateId,
    size,
    supportedEventTypes: eventTypes,
    timezone: timezoneId ?? getLocalTimeZone(),
  });
};

function toMoment(date: Date | string | number, timeZone: string) {
  if (timeZone) {
    return moment.tz(date, timeZone);
  }
  return moment(date);
}

// NOTE: If how we query changes, we may need to also revise `_getExpiry`,
// to take into account those changes (e.g., may need to factor `origin` into its logic).
// See SOL-1310.
export const getHistogramParams = ({
  eventStandard,
  featureStandard,
  canBeCached,
  compareVsAllCustomerLocations,
  date: sourceDate,
  eventTypes,
  fields,
  locationIds,
  siteIds,
  range,
  rangeRolling,
  reportTemplateId,
  include,
  eventType,
  eventSubtypeFilter,
  eventTagsFilter,
  timezoneId,
  units,
  searchString,
  searchFilters,
  useSourceDate,
  searchOperator,
}: {
  eventStandard: EventStandardDefs;
  featureStandard: FeatureStandardDefs;
  canBeCached?: boolean;
  compareVsAllCustomerLocations?: boolean;
  date: Date;
  eventTypes?: readonly string[] | { readonly [type: string]: boolean } | EventFilterState;
  fields?: readonly string[];
  /** @deprecated Use `siteIds` instead (note locationIds should be site IDs and not device IDs if provided) */
  locationIds?: readonly string[];
  siteIds?: readonly string[];
  range: HistogramRange;
  rangeRolling?: boolean;
  reportTemplateId?: string;
  include?: string;
  eventType?: string;
  eventSubtypeFilter?: { [subType: string]: boolean };
  eventTagsFilter?: readonly string[] | undefined;
  timezoneId: string;
  units?: IUnitsAndCount;
  searchString?: string;
  searchFilters?: readonly Filter[];
  useSourceDate?: boolean;
  searchOperator?: SearchOperator;
}): IHistogramApiParams => {
  let origin: 'start' | 'end' = 'start';
  let date = toMoment(sourceDate, timezoneId)
    .startOf(histogramRangeToMomentDuration(range))
    .toDate();

  if (rangeRolling) {
    origin = 'end';

    if (useSourceDate || range === HISTOGRAM_RANGE.DAY) {
      // date is already correctly offset for date filters so use original source date
      date = sourceDate;
    } else {
      date = toMoment(sourceDate, timezoneId)
        .endOf(HistogramApiHelper.getBucketSize(range))
        .toDate();
    }
  } else if (HistogramApiHelper.isMonthInWeeks({ range, units })) {
    origin = 'end';
    date = toMoment(sourceDate, timezoneId).endOf('week').toDate();
  }

  let includeTags = include ? include.split(',') : [];
  for (const field of fields || []) {
    const queryTags = eventStandard[field]?.features?.histogramQueryTags || [];

    if (queryTags.length > 0) {
      includeTags = includeTags.concat(queryTags);
    }
  }

  const eventFilterStoreReportId = EventFilterStore.getSelectedReportId({ checkInitial: true });
  if (reportTemplateId && reportTemplateId !== ALL_EVENTS_REPORT) {
    const { reportHistogramTags } = EventFilterStore.getReportFilters(
      featureStandard,
      reportTemplateId,
    );
    includeTags = includeTags.concat(reportHistogramTags);
  }

  includeTags = uniq(includeTags);

  let report;
  if (reportTemplateId && reportTemplateId !== ALL_EVENTS_REPORT) {
    report = EventFilterStore.getSelectedReportById(reportTemplateId);
  }

  const eq = HistogramApiHelper.generateHistogramQuery({
    eventStandard,
    featureStandard,
    eventTypes:
      eventType ||
      (eventTypes as readonly string[] | { readonly [type: string]: boolean } | undefined),
    siteIds: siteIds ? siteIds : locationIds,
    fields,
    reports: report ? [report] : undefined,
    eventSubtypeFilter,
  });

  if (eventTagsFilter) {
    applyTagsFilter(eq, eventTagsFilter);
  }

  addSearchToQuery({
    eq,
    searchString,
    searchFilters,
    searchOperator,
  });

  return HistogramApiHelper.generateHistogramApiParams({
    compareVsAllCustomerLocations: isEmpty(locationIds) && compareVsAllCustomerLocations,
    date,
    origin,
    expires: _getExpiry(canBeCached, date, range, timezoneId),
    fields,
    includeTags,
    esQuery: eq,
    range,
    rangeRolling,
    reportTemplateId:
      reportTemplateId && reportTemplateId !== ALL_EVENTS_REPORT
        ? reportTemplateId
        : eventFilterStoreReportId,
    timeFieldToSearchOn: range === HISTOGRAM_RANGE.HOUR ? 'endTime' : 'startTime',
    timezone: timezoneId ?? getLocalTimeZone(),
    units,
  });
};

export const getLocalizedRangeLabel = ({
  range,
  rangeRolling,
  locale,
}: {
  range: HistogramRange;
  rangeRolling: boolean;
  locale: Pick<LocaleDefinition, 'date' | 'histogram'>;
}) => {
  const localeKey = range.toLowerCase() as keyof LocaleDefinition['date'];
  if (rangeRolling) {
    let histogramKeyToShow: keyof LocaleDefinition['histogram'] = 'lastTwelveMonths';
    switch (localeKey) {
      case 'hour':
        histogramKeyToShow = 'lastSixtyMinutes';
        break;
      case 'day':
        histogramKeyToShow = 'lastTwentyFourHours';
        break;
      case 'week':
        histogramKeyToShow = 'lastSevenDays';
        break;
      case 'month':
        histogramKeyToShow = 'lastFourWeeks';
        break;
      case 'year':
      default:
        histogramKeyToShow = 'lastTwelveMonths';
        break;
    }
    return locale.histogram[histogramKeyToShow];
  }
  return locale.date[localeKey];
};

const _getExpiry = (
  canBeCached: boolean | undefined,
  date: Date | string | number,
  range: HistogramRange,
  timeZone: string,
) => {
  const hasCurrentDayData = FeaturesUtil.hasCurrentDayData();
  const endDate = toMoment(date, timeZone)
    .add(1, histogramRangeToMomentDuration(range))
    .subtract(1, 'second');
  const today = toMoment(Date.now(), timeZone).startOf('day');
  const isBeforeToday = endDate.isBefore(today, 'day');
  if (hasCurrentDayData && isBeforeToday) {
    return expiresIn(1).years();
  } else if (!hasCurrentDayData) {
    const yesterday = today.clone().subtract(1, 'day').startOf('day');
    const isBeforeYesterday = endDate.isBefore(yesterday, 'day');
    if (isBeforeYesterday) {
      return expiresIn(1).years();
    }
  }
  return canBeCached ? expiresIn(15).minutes() : 0;
};
